import React, { useState } from 'react';
import { Briefcase, Clock, ArrowRight, Home, Mail, Phone, Users, Heart, GraduationCap, MapPin, Calendar, Star, Award, Globe, Target, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';

const CarrierePage = () => {
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  // Écouter les changements de langue
  React.useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Contenu traduit
  const content = {
    fr: {
      title: "Carrières & Opportunités",
      subtitle: "Rejoignez notre équipe et participez à la transformation des communautés",
      heroDescription: "Chez Action Humanitaire Bénin, nous croyons que chaque personne a le pouvoir de changer le monde. Découvrez comment vous pouvez contribuer à notre mission humanitaire.",
      
      // Section Bénévolat
      volunteerTitle: "Bénévolat",
      volunteerSubtitle: "Donnez de votre temps pour une cause noble",
      volunteerDescription: "Le bénévolat est au cœur de notre organisation. Rejoignez notre équipe de bénévoles dévoués et participez à nos projets sur le terrain.",
      volunteerBenefits: [
        "Expérience terrain enrichissante",
        "Formation en action humanitaire",
        "Réseau de professionnels engagés",
        "Impact direct sur les communautés"
      ],
      
      // Section Pourquoi nous rejoindre
      whyJoinTitle: "Pourquoi nous rejoindre ?",
      whyJoinDescription: "Découvrez ce qui fait d'AHB un employeur exceptionnel",
      whyJoinReasons: [
        {
          icon: Heart,
          title: "Mission significative",
          description: "Contribuez à des projets qui changent des vies"
        },
        {
          icon: Users,
          title: "Équipe passionnée",
          description: "Travaillez avec des professionnels dévoués"
        },
        {
          icon: Globe,
          title: "Impact international",
          description: "Participez à des projets d'envergure"
        },
        {
          icon: Award,
          title: "Reconnaissance",
          description: "Votre contribution sera valorisée"
        }
      ],
      
      // Section Contact
      contactTitle: "Intéressé ? Contactez-nous !",
      contactDescription: "Envoyez-nous votre CV et lettre de motivation. Nous étudierons votre profil avec attention.",
      sendCV: "Envoyer mon CV",
      contactInfo: "Informations de contact",
      
      // Boutons et liens
      backHome: "Retour à l'accueil",
      contactUs: "Nous contacter",
      aboutUs: "À propos de nous",
      ourProjects: "Nos projets",
      makeDonation: "Faire un don",
      aboutUsDesc: "Découvrez notre mission et nos valeurs",
      projectsDesc: "Explorez nos actions humanitaires",
      donationDesc: "Soutenez nos actions dès maintenant"
    },
    en: {
      title: "Careers & Opportunities",
      subtitle: "Join our team and participate in community transformation",
      heroDescription: "At Action Humanitaire Benin, we believe that every person has the power to change the world. Discover how you can contribute to our humanitarian mission.",
      
      // Section Bénévolat
      volunteerTitle: "Volunteering",
      volunteerSubtitle: "Give your time for a noble cause",
      volunteerDescription: "Volunteering is at the heart of our organization. Join our dedicated volunteer team and participate in our field projects.",
      volunteerBenefits: [
        "Enriching field experience",
        "Humanitarian action training",
        "Network of engaged professionals",
        "Direct impact on communities"
      ],
      
      // Section Pourquoi nous rejoindre
      whyJoinTitle: "Why join us?",
      whyJoinDescription: "Discover what makes AHB an exceptional employer",
      whyJoinReasons: [
        {
          icon: Heart,
          title: "Meaningful mission",
          description: "Contribute to projects that change lives"
        },
        {
          icon: Users,
          title: "Passionate team",
          description: "Work with dedicated professionals"
        },
        {
          icon: Globe,
          title: "International impact",
          description: "Participate in large-scale projects"
        },
        {
          icon: Award,
          title: "Recognition",
          description: "Your contribution will be valued"
        }
      ],
      
      // Section Contact
      contactTitle: "Interested? Contact us!",
      contactDescription: "Send us your CV and cover letter. We will carefully review your profile.",
      sendCV: "Send my CV",
      contactInfo: "Contact information",
      
      // Boutons et liens
      backHome: "Back to home",
      contactUs: "Contact us",
      aboutUs: "About us",
      ourProjects: "Our projects",
      makeDonation: "Make a donation",
      aboutUsDesc: "Discover our mission and values",
      projectsDesc: "Explore our humanitarian actions",
      donationDesc: "Support our actions now"
    }
  };

  const t = (key) => {
    const lang = currentLanguage === 'en' ? 'en' : 'fr';
    return key.split('.').reduce((obj, k) => obj && obj[k], content[lang]) || key;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-12 bg-gradient-to-br from-[#089556] via-[#07D1EB] to-[#0ba8c7] overflow-hidden">
        {/* Molecular Background Pattern */}
        <div className="absolute inset-0 opacity-[0.15]">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 2px, transparent 2px),
              radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 3px, transparent 3px),
              radial-gradient(circle at 40% 80%, rgba(255,255,255,0.7) 2.5px, transparent 2.5px),
              radial-gradient(circle at 70% 20%, rgba(255,255,255,0.5) 3.5px, transparent 3.5px)
            `,
            backgroundSize: '180px 180px, 140px 140px, 200px 200px, 160px 160px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto shadow-lg border border-white/30">
              <Briefcase className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-lg">
            {t('title')}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
            {t('subtitle')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        


        {/* Section Bénévolat */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              {t('volunteerTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('volunteerSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                {t('volunteerTitle')}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('volunteerDescription')}
              </p>
              
              <div className="space-y-3">
                {t('volunteerBenefits').map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-[#089556] rounded-full"></div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-2xl p-8 text-white shadow-lg">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">
                Rejoignez notre équipe
              </h3>
              <p className="text-white/90 mb-6 leading-relaxed">
                Nous recherchons des bénévoles passionnés dans tous les domaines : 
                santé, éducation, environnement, logistique, communication...
              </p>
              <button className="bg-white text-[#089556] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200">
                Devenir bénévole
              </button>
            </div>
          </div>
        </section>



        {/* Section Pourquoi nous rejoindre */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              {t('whyJoinTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('whyJoinDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t('whyJoinReasons').map((reason, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <div className="w-14 h-14 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <reason.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-3">
                  {reason.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section Contact */}
        <section className="mb-20">
          <div className="bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-2xl p-8 text-white shadow-lg">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t('contactTitle')}
              </h2>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                {t('contactDescription')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <button className="w-full bg-white text-[#089556] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors duration-200 mb-6">
                  {t('sendCV')}
                </button>
                <p className="text-white/80 text-sm">
                  Envoyez votre CV et lettre de motivation à : contact@ahbenin.org
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-4">
                  {t('contactInfo')}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-white/80" />
                    <span>+229 01 55 07 81 81</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-white/80" />
                    <span>contact@ahbenin.org</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-white/80" />
                    <span>Cotonou, Bénin</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link 
              to="/about"
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:shadow-lg transition-all duration-200 group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">
                {t('aboutUs')}
              </h3>
              <p className="text-gray-600 text-sm text-center">
                {t('aboutUsDesc')}
              </p>
            </Link>

            <Link 
              to="/projets"
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:shadow-lg transition-all duration-200 group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">
                {t('ourProjects')}
              </h3>
              <p className="text-gray-600 text-sm text-center">
                {t('projectsDesc')}
              </p>
            </Link>

            <Link 
              to="/don"
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:shadow-lg transition-all duration-200 group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">
                {t('makeDonation')}
              </h3>
              <p className="text-gray-600 text-sm text-center">
                {t('donationDesc')}
              </p>
            </Link>
          </div>
        </section>

        {/* Action Buttons */}
        <section className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <Home className="w-5 h-5 mr-2" />
              {t('backHome')}
            </Link>
            
            <Link 
              to="/contacts"
              className="inline-flex items-center px-8 py-4 bg-white text-[#089556] border-2 border-[#089556] rounded-lg font-medium hover:bg-[#089556] hover:text-white transition-all duration-200"
            >
              <Mail className="w-5 h-5 mr-2" />
              {t('contactUs')}
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default CarrierePage; 